import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Store, CheckCircle, Clock, XCircle, Building2, User, CreditCard, MapPin, Phone, Mail, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SellerApplyPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Firma Bilgileri
    company_name: '',
    store_name: '',
    store_description: '',
    company_type: 'sahis', // sahis, limited, anonim
    tax_number: '',
    tax_office: '',
    
    // Yetkili Kişi Bilgileri
    authorized_name: '',
    authorized_surname: '',
    authorized_tc: '',
    authorized_phone: '',
    authorized_email: '',
    
    // Banka Bilgileri
    bank_name: '',
    iban: '',
    account_holder: '',
    
    // Adres Bilgileri
    address: '',
    city: '',
    district: '',
    postal_code: '',
    
    // İletişim
    phone: '',
    email: '',
    website: ''
  });

  useEffect(() => {
    checkApplicationStatus();
  }, []);

  const checkApplicationStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/seller/application-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setApplicationStatus(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/seller/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Başvurunuz alındı! En kısa sürede değerlendireceğiz.');
        checkApplicationStatus();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Başvuru başarısız');
      }
    } catch (err) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  if (checkingStatus) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  // Zaten satıcıysa
  if (user?.user_type === 'seller') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Zaten Satıcısınız!</h1>
        <p className="text-gray-600 mb-6">Satıcı panelinize giderek ürünlerinizi yönetebilirsiniz.</p>
        <button
          onClick={() => navigate('/seller/dashboard')}
          className="bg-orange-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-600"
        >
          Satıcı Paneline Git
        </button>
      </div>
    );
  }

  // Bekleyen başvuru varsa
  if (applicationStatus?.status === 'pending') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">Başvurunuz İnceleniyor</h1>
        <p className="text-gray-600 mb-4">
          <strong>{applicationStatus.store_name || applicationStatus.company_name}</strong> için başvurunuz değerlendiriliyor.
        </p>
        <p className="text-sm text-gray-500">
          Başvuru tarihi: {new Date(applicationStatus.created_at).toLocaleDateString('tr-TR')}
        </p>
      </div>
    );
  }

  // Reddedildiyse
  if (applicationStatus?.status === 'rejected') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Başvurunuz Reddedildi</h1>
          {applicationStatus.rejection_reason && (
            <p className="text-gray-600">Sebep: {applicationStatus.rejection_reason}</p>
          )}
        </div>
        <p className="text-center text-gray-500 mb-8">
          Yeni bir başvuru yapabilirsiniz:
        </p>
        <ApplicationFormFull 
          formData={formData} 
          setFormData={setFormData} 
          handleSubmit={handleSubmit} 
          loading={loading}
          step={step}
          setStep={setStep}
          nextStep={nextStep}
          prevStep={prevStep}
        />
      </div>
    );
  }

  // Yeni başvuru
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <Store className="w-16 h-16 text-orange-500 mx-auto mb-4" />
        <h1 className="text-3xl font-bold mb-2">Satıcı Başvurusu</h1>
        <p className="text-gray-600">
          Pazarlo'da mağazanızı açın, binlerce müşteriye ulaşın!
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-8">
        {[
          { num: 1, label: 'Firma Bilgileri', icon: Building2 },
          { num: 2, label: 'Yetkili Kişi', icon: User },
          { num: 3, label: 'Banka Bilgileri', icon: CreditCard },
          { num: 4, label: 'İletişim & Adres', icon: MapPin }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div 
              className={"flex flex-col items-center cursor-pointer"}
              onClick={() => setStep(s.num)}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                step >= s.num 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                <s.icon className="w-5 h-5" />
              </div>
              <span className={`text-xs mt-2 font-medium ${step >= s.num ? 'text-orange-600' : 'text-gray-400'}`}>
                {s.label}
              </span>
            </div>
            {i < 3 && (
              <div className={`w-16 h-1 mx-2 rounded ${step > s.num ? 'bg-orange-500' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="bg-orange-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-orange-800 mb-2">Satıcı Avantajları</h3>
        <ul className="text-sm text-orange-700 grid grid-cols-2 gap-2">
          <li>✓ 158.000+ ürün kataloğu</li>
          <li>✓ Kendi fiyatınızı belirleyin</li>
          <li>✓ Türkiye genelinde satış</li>
          <li>✓ Sadece %10 komisyon</li>
        </ul>
      </div>

      <ApplicationFormFull 
        formData={formData} 
        setFormData={setFormData} 
        handleSubmit={handleSubmit} 
        loading={loading}
        step={step}
        setStep={setStep}
        nextStep={nextStep}
        prevStep={prevStep}
      />
    </div>
  );
}

function ApplicationFormFull({ formData, setFormData, handleSubmit, loading, step, nextStep, prevStep }) {
  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
      
      {/* Step 1: Firma Bilgileri */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Firma Bilgileri</h2>
              <p className="text-sm text-gray-500">İşletmenizin resmi bilgilerini girin</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Firma Türü *</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'sahis', label: 'Şahıs Şirketi' },
                { value: 'limited', label: 'Limited Şirket' },
                { value: 'anonim', label: 'Anonim Şirket' }
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateField('company_type', opt.value)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    formData.company_type === opt.value
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Firma / Şirket Adı *</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => updateField('company_name', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Örn: ABC Otomotiv Ltd. Şti."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Mağaza Adı (Pazarlo'da görünecek) *</label>
              <input
                type="text"
                value={formData.store_name}
                onChange={(e) => updateField('store_name', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Örn: ABC Otomotiv"
              />
              {formData.store_name && formData.company_name && (
                formData.store_name.trim().toLowerCase() === formData.company_name.trim().toLowerCase() ||
                formData.store_name.trim().toLowerCase().includes(formData.company_name.trim().toLowerCase()) ||
                formData.company_name.trim().toLowerCase().includes(formData.store_name.trim().toLowerCase())
              ) && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span>&#9888;</span> Magaza adi firma adi ile ayni veya benzer olamaz. Lutfen farkli bir isim secin.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Mağaza Açıklaması</label>
            <textarea
              value={formData.store_description}
              onChange={(e) => updateField('store_description', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Mağazanız hakkında kısa bir açıklama yazın..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Vergi Numarası *</label>
              <input
                type="text"
                value={formData.tax_number}
                onChange={(e) => updateField('tax_number', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="10 haneli vergi numarası"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Vergi Dairesi *</label>
              <input
                type="text"
                value={formData.tax_office}
                onChange={(e) => updateField('tax_office', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Örn: Kadıköy Vergi Dairesi"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Yetkili Kişi */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Yetkili Kişi Bilgileri</h2>
              <p className="text-sm text-gray-500">Firma yetkilisinin iletişim bilgileri</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Yetkili Adı *</label>
              <input
                type="text"
                value={formData.authorized_name}
                onChange={(e) => updateField('authorized_name', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Adı"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Yetkili Soyadı *</label>
              <input
                type="text"
                value={formData.authorized_surname}
                onChange={(e) => updateField('authorized_surname', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Soyadı"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">T.C. Kimlik Numarası *</label>
            <input
              type="text"
              value={formData.authorized_tc}
              onChange={(e) => updateField('authorized_tc', e.target.value)}
              required
              maxLength={11}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="11 haneli TC Kimlik No"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Yetkili Telefon *</label>
              <input
                type="tel"
                value={formData.authorized_phone}
                onChange={(e) => updateField('authorized_phone', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="05XX XXX XX XX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Yetkili E-posta *</label>
              <input
                type="email"
                value={formData.authorized_email}
                onChange={(e) => updateField('authorized_email', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="yetkili@firma.com"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Banka Bilgileri */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Banka Bilgileri</h2>
              <p className="text-sm text-gray-500">Ödemelerinizin yapılacağı hesap bilgileri</p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>Önemli:</strong> IBAN bilgisi firma adına kayıtlı olmalıdır. Şahıs şirketlerinde firma sahibi adına olabilir.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Banka Adı *</label>
            <select
              value={formData.bank_name}
              onChange={(e) => updateField('bank_name', e.target.value)}
              required
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="">Banka Seçin</option>
              <option value="Ziraat Bankası">Ziraat Bankası</option>
              <option value="İş Bankası">İş Bankası</option>
              <option value="Garanti BBVA">Garanti BBVA</option>
              <option value="Yapı Kredi">Yapı Kredi</option>
              <option value="Akbank">Akbank</option>
              <option value="Halkbank">Halkbank</option>
              <option value="Vakıfbank">Vakıfbank</option>
              <option value="QNB Finansbank">QNB Finansbank</option>
              <option value="Denizbank">Denizbank</option>
              <option value="TEB">TEB</option>
              <option value="ING Bank">ING Bank</option>
              <option value="HSBC">HSBC</option>
              <option value="Şekerbank">Şekerbank</option>
              <option value="Enpara">Enpara</option>
              <option value="Papara">Papara</option>
              <option value="Diğer">Diğer</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">IBAN *</label>
            <input
              type="text"
              value={formData.iban}
              onChange={(e) => updateField('iban', e.target.value.toUpperCase())}
              required
              maxLength={26}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono"
              placeholder="TR00 0000 0000 0000 0000 0000 00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Hesap Sahibi Adı *</label>
            <input
              type="text"
              value={formData.account_holder}
              onChange={(e) => updateField('account_holder', e.target.value)}
              required
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="IBAN'daki isim ile aynı olmalı"
            />
          </div>
        </div>
      )}

      {/* Step 4: İletişim & Adres */}
      {step === 4 && (
        <div className="space-y-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold">İletişim & Adres Bilgileri</h2>
              <p className="text-sm text-gray-500">Firmanızın iletişim ve adres bilgileri</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">İş Telefonu *</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="0XXX XXX XX XX"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">İş E-postası *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="info@firma.com"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Website (Opsiyonel)</label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => updateField('website', e.target.value)}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="https://www.firma.com"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">İl *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => updateField('city', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="İstanbul"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">İlçe *</label>
              <input
                type="text"
                value={formData.district}
                onChange={(e) => updateField('district', e.target.value)}
                required
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Kadıköy"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Posta Kodu</label>
              <input
                type="text"
                value={formData.postal_code}
                onChange={(e) => updateField('postal_code', e.target.value)}
                className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="34000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Açık Adres *</label>
            <textarea
              value={formData.address}
              onChange={(e) => updateField('address', e.target.value)}
              required
              rows={3}
              className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Mahalle, Cadde/Sokak, Bina No, Daire No..."
            />
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-8 pt-6 border-t">
        {step > 1 ? (
          <button
            type="button"
            onClick={prevStep}
            className="px-6 py-3 border-2 border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition-colors"
          >
            ← Geri
          </button>
        ) : (
          <div />
        )}
        
        {step < 4 ? (
          <button
            type="button"
            onClick={nextStep}
            className="px-8 py-3 bg-orange-500 text-white rounded-xl font-medium hover:bg-orange-600 transition-colors"
          >
            Devam →
          </button>
        ) : (
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Gönderiliyor...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Başvuruyu Gönder
              </>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
